
import type { AccessTokenData } from '@empathyco/playboard-core-domain'
import type { UserRoleManagementMessages } from '../models'
import { computed, defineComponent, getCurrentInstance, watch } from 'vue'
import { EpbButton } from '../components/shared/epb-button'
import { EpbTextbox } from '../components/shared/epb-textbox'
import ChangePasswordModal from '../components/users/change-password-modal.component.vue'
import { useAppLoading } from '../composables/use-app-loading'
import { useUsers } from '../composables/users/use-users'
import { useUsersError } from '../composables/users/use-users-error'
import { mapGetters } from '../composables/utils'
import { AUTH_CONSTS } from '../store/auth/auth.consts'

export default defineComponent({
  name: 'Account',
  components: {
    ChangePasswordModal,
    EpbButton,
    EpbTextbox,
  },
  setup(props, { emit }) {
    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const { tokenInfo } = mapGetters<AccessTokenData>(ctx.proxy.$store, AUTH_CONSTS.moduleName, {
      tokenInfo: AUTH_CONSTS.getters.tokenInfo,
    })

    const messages = ctx.proxy.$epb.messages.userRoleManagement
      .account as UserRoleManagementMessages['account']

    const {
      loading: { read: loading },
      data: { read: user },
      error: { read: error },
      read,
    } = useUsers()
    const { setLoading } = useAppLoading()
    const { getErrorMessage } = useUsersError({ messages })

    const baseClass = 'epb-account'

    const id = computed(() => Number(tokenInfo.value.user.id))

    async function save(): Promise<void> {
      setLoading(true)
      try {
        await user.value.save()
        emit('showSnackbar', messages.update.success)
      } catch ([error]) {
        emit('showError', getErrorMessage(error.payload))
      } finally {
        setLoading(false)
      }
    }

    watch(
      id,
      value => {
        read(value)
      },
      { immediate: true },
    )

    watch(loading, value => {
      setLoading(value)
    })

    watch(error, value => {
      if (value) {
        emit('showError', getErrorMessage(value))
      }
    })

    return {
      messages,
      baseClass,
      user,
      save,
    }
  },
})
